import { useCallback, useEffect, useMemo, useState } from "react";
import { Banknote, Clock, CheckCircle2, XCircle, RotateCcw, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const usd = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  pending: { label: "Pending", cls: "text-amber-300 border-amber-400/40 bg-amber-500/10", Icon: Clock },
  completed: { label: "Paid", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "text-rose-300 border-rose-400/40 bg-rose-500/10", Icon: XCircle },
  reversed: { label: "Reversed · balance restored", cls: "text-sky-300 border-sky-400/40 bg-sky-500/10", Icon: RotateCcw },
};

export function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <span data-testid={`cashout-status-${status}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-2 py-0.5 ${s.cls}`}><s.Icon size={11} /> {s.label}</span>;
}

export default function CashOutPanel() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [coins, setCoins] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idk, setIdk] = useState(() => crypto.randomUUID());
  const [method, setMethod] = useState("scheduled");

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.get("/cashout/summary"), api.get("/cashout/history")]);
      setSummary(s.data); setHistory(h.data.cashouts || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load cash-out."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const n = Math.max(0, parseInt(coins || "0", 10) || 0);
  const quote = useMemo(() => {
    const per = summary?.coins_per_cent || 4;
    const cents = Math.floor(n / per);
    return { cents, debited: cents * per, retained: n - cents * per };
  }, [n, summary]);
  const tooSmall = n < (summary?.min_coins || 400);
  const overCap = summary && n > summary.cashable_coins;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cashout", { coins: n, idempotency_key: idk, method });
      toast.success(`Cash-out of ${usd(data.cents)} requested — status Pending.`);
      setConfirm(false); setCoins(""); setIdk(crypto.randomUUID());
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cash-out failed."); }
    finally { setBusy(false); }
  };

  if (!summary) return <div className="text-sm text-zinc-500" data-testid="cashout-loading">Loading cash-out…</div>;
  return (
    <div className="space-y-6" data-testid="cashout-panel">
      {summary.enabled && summary.provider_mode === "stripe" && !summary.kyc?.ok && (
        <div className="border border-sky-500/40 bg-sky-500/[0.07] p-4 text-sm text-sky-100 flex gap-3" data-testid="cashout-kyc-banner">
          <Info size={16} className="shrink-0 mt-0.5" />
          <div>Finish payout onboarding first — identity and tax details are collected by Stripe (Creator → Payouts). Cash-outs unlock automatically once your account is verified.</div>
        </div>
      )}
      {!summary.enabled && (
        <div className="border border-amber-500/40 bg-amber-500/[0.07] p-4 text-sm text-amber-200 flex gap-3" data-testid="cashout-disabled-banner">
          <Lock size={16} className="shrink-0 mt-0.5" />
          <div>Cash-out is not switched on yet. Your eligible balance keeps accruing; withdrawals open once payout, identity and tax checks are approved.</div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-surface p-5 sm:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Available to cash out</div>
          <div className="font-display text-4xl mt-1" data-testid="cashout-available-usd">{usd(summary.cashable_cents)}</div>
          <div className="text-xs text-zinc-400 mt-1" data-testid="cashout-available-coins">{summary.cashable_coins.toLocaleString()} eligible coins{summary.coins_retained_if_all ? ` · ${summary.coins_retained_if_all} coin${summary.coins_retained_if_all > 1 ? "s" : ""} stay until the next whole cent` : ""}</div>
          <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed" data-testid="cashout-explainer">
            <Info size={11} className="inline mr-1 -mt-0.5" /><strong className="text-zinc-300">{summary.coins_per_usd} coins = $1.00.</strong> When a fan gifts you or spins your wheel, MyPandaX keeps {summary.platform_pct}% and your {summary.creator_pct}% (after any ambassador commission) becomes eligible here — the split happens once, never again at cash-out. Only whole cents are paid; leftover coins stay in your balance.
          </div>
        </div>
        <div className="card-surface p-5 text-xs space-y-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Lifetime</div>
          <div className="flex justify-between"><span className="text-zinc-400">Eligible earned</span><span data-testid="cashout-lifetime-earned">{summary.lifetime_eligible_earned.toLocaleString()} coins</span></div>
          {["pending", "completed", "failed", "reversed"].map((k) => (
            <div key={k} className="flex justify-between"><span className="text-zinc-400 capitalize">{STATUS[k].label}</span><span data-testid={`cashout-total-${k}`}>{usd(summary.totals[k]?.cents || 0)}</span></div>
          ))}
        </div>
      </div>

      <div className="card-surface p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Request a cash-out</div>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
          <div className="min-w-[160px]">
            <label className="text-xs text-zinc-400 whitespace-nowrap" htmlFor="cashout-coins">Coins to cash out (min {summary.min_coins})</label>
            <Input id="cashout-coins" data-testid="cashout-coins-input" inputMode="numeric" value={coins} onChange={(e) => setCoins(e.target.value.replace(/[^0-9]/g, ""))} placeholder={String(summary.cashable_coins)} className="mt-1 bg-black/40 border-white/10 rounded-none" />
          </div>
          <select data-testid="cashout-method-select" value={method} onChange={(e) => setMethod(e.target.value)} className="bg-black/40 border border-white/10 text-sm text-zinc-200 px-3 py-2 rounded-none">
            <option value="scheduled">Scheduled payout (default, 2–3 business days)</option>
            <option value="instant">Instant payout (when eligible)</option>
          </select>
          <Button variant="outline" data-testid="cashout-max-btn" onClick={() => setCoins(String(summary.cashable_coins))} className="rounded-none border-white/15 text-zinc-200">Max</Button>
          <Button data-testid="cashout-review-btn" disabled={!summary.enabled || tooSmall || overCap} onClick={() => setConfirm(true)} className="btn-crimson rounded-none px-6"><Banknote size={14} className="mr-2" /> Review cash-out</Button>
        </div>
        <div className="text-xs text-zinc-400 mt-2" data-testid="cashout-quote">
          {n > 0 ? <>You&apos;d receive <span className="text-white">{usd(quote.cents)}</span> for {quote.debited.toLocaleString()} coins{quote.retained ? <> · <span className="text-amber-300">{quote.retained} coin{quote.retained > 1 ? "s" : ""} stay in your balance</span></> : null}{overCap && <span className="text-rose-300"> · exceeds your available balance</span>}</> : "Enter an amount to see the exact dollar value."}
        </div>
      </div>

      <div className="card-surface p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Payout history</div>
        {history.length === 0 ? <div className="text-sm text-zinc-500" data-testid="cashout-history-empty">No cash-outs yet.</div> : (
          <div className="divide-y divide-white/5" data-testid="cashout-history">
            {history.map((h) => (
              <div key={h.id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid={`cashout-row-${h.id}`}>
                <div className="min-w-0">
                  <div className="text-white">{usd(h.cents)} <span className="text-zinc-500 text-xs">· {h.coins_debited.toLocaleString()} coins{h.coins_retained ? ` · ${h.coins_retained} retained` : ""}</span></div>
                  <div className="text-[11px] text-zinc-500">{new Date(h.created_at).toLocaleString()} · {h.provider?.method || h.payout_method || "scheduled"}{h.provider?.transfer_id ? ` · ${h.provider.transfer_id}` : ""}{h.error ? ` · ${h.error}` : ""}</div>
                </div>
                <StatusChip status={h.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white rounded-none max-w-sm" data-testid="cashout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Confirm cash-out</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">Review before we create the payout instruction.</DialogDescription>
          </DialogHeader>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-zinc-500">Coins</dt><dd data-testid="cashout-confirm-coins">{quote.debited.toLocaleString()}</dd>
            <dt className="text-zinc-500">You receive</dt><dd data-testid="cashout-confirm-usd" className="text-emerald-300 font-medium">{usd(quote.cents)}</dd>
            <dt className="text-zinc-500">Rate</dt><dd>{summary.coins_per_usd} coins = $1.00</dd>
            <dt className="text-zinc-500">Payout</dt><dd data-testid="cashout-confirm-method">{method === "instant" ? "Instant (falls back to scheduled if not eligible)" : "Scheduled — Stripe's standard payout schedule"}</dd>
            {quote.retained > 0 && <><dt className="text-zinc-500">Stays in balance</dt><dd data-testid="cashout-confirm-retained">{quote.retained} coin{quote.retained > 1 ? "s" : ""}</dd></>}
          </dl>
          <p className="text-[11px] text-zinc-500">This cannot be undone once the payout is sent. If the payout fails, the coins are returned to your balance automatically.</p>
          <Button disabled={busy} onClick={submit} data-testid="cashout-confirm-btn" className="btn-crimson rounded-none w-full py-5">{busy ? "Requesting…" : `Cash out ${usd(quote.cents)}`}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
